// src/pages/ThankYouPage.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";

function ThankYouPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);

  const thankYouTrackedRef = useRef(false);

  useEffect(() => {
    const data = sessionStorage.getItem("esill-order");
    if (data) setOrder(JSON.parse(data));
  }, []);

  useEffect(() => {
  if (!order?.order_id) return;

  const trackingKey = `esill-thank-you-tracked-${order.order_id}`;

  // ✅ Prevent duplicate tracking from React StrictMode / remount / refresh
  if (thankYouTrackedRef.current || sessionStorage.getItem(trackingKey)) {
    return;
  }

  thankYouTrackedRef.current = true;
  sessionStorage.setItem(trackingKey, "1");

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_thank_you",
      entity_type: "order",
      entity_id: order.order_id,
    }),
  }).catch(() => {});
}, [order?.order_id]);

  if (!order) {
  return (
    <div
      className="w-full min-h-screen bg-[#F6EFE9]"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
        <div className="max-w-[900px] mx-auto px-5 md:px-6 py-16 md:py-20 text-center">
          <div className="mx-auto w-[54px] h-[54px] rounded-full bg-black text-white flex items-center justify-center text-[28px]">
            !
          </div>

          <h1 className="mt-5 text-[30px] md:text-[36px] leading-none font-bold text-[#333]">
            {lang === "AR" ? "لا يوجد طلب لعرضه" : "No order to display"}
          </h1>

          <p className="mt-4 text-[14px] md:text-[15px] text-[#666] leading-relaxed">
            {lang === "AR"
              ? "لم نتمكن من العثور على تفاصيل الطلب. يمكنك العودة إلى المتجر ومتابعة التسوق."
              : "We could not find order details. You can return to the store and continue shopping."}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full sm:w-[220px] h-[50px] bg-[#e99a22] text-white text-[15px] font-normal hover:opacity-90 active:scale-[0.99] transition"
            >
              {lang === "AR" ? "العودة إلى المتجر" : "Back to store"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/cart")}
              className="w-full sm:w-[220px] h-[50px] bg-white border border-black text-black text-[15px] font-normal hover:bg-[#f7f7f7] transition"
            >
              {lang === "AR" ? "الذهاب إلى السلة" : "Go to cart"}
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

  const TEXT = {
    EN: {
      thanks: "Thank You!",
      loginInfo: "These are your CHIACA account login details:",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email",
      noEmail: "No email provided",
      city: "Governorate",
      address: "Address",
      credentialsNote:
  "We will use this information to prepare your CHIACA customer account for future logins.",
      ok: "OK",
      summary: "Order Summary",
      products: "Products",
      delivery: "Delivery",
      total: "Total",
    },
    AR: {
      thanks: "شكرًا لك!",
      loginInfo: "هذه هي بيانات تسجيل الدخول الخاصة بحسابك في CHIACA:",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني",
      noEmail: "لم يتم إدخال بريد إلكتروني",
      city: "المحافظة",
      address: "العنوان",
      credentialsNote:
  "سنستخدم هذه المعلومات لإنشاء حساب زبون لك في CHIACA لتسجيل الدخول مستقبلاً.",
      ok: "حسنًا",
      summary: "ملخص الطلب",
      products: "عدد المنتجات",
      delivery: "التوصيل",
      total: "الإجمالي",
    },
  };

  const t = TEXT[lang];

  const getDeliveryMessage = () => {
    if (order.shipping_method === "pickup") {
      return lang === "EN"
        ? "Your order will be ready for pickup from the store"
        : "طلبك سيكون جاهزاً للاستلام من المتجر";
    }

    return lang === "EN"
      ? "Your order will be delivered within 2 to 3 days"
      : "سيتم توصيل طلبك خلال يومين إلى ثلاثة أيام";
  };

 const cardStyle =
  "w-full bg-[#F6EFE9] border border-[#e6e6e6] px-5 py-6 md:px-7 md:py-7";

  return (
  <div
    className="w-full min-h-screen bg-[#F6EFE9]"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    {/* TITLE */}
    <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 py-12 text-center">
        <div className="mx-auto w-[54px] h-[54px] rounded-full bg-black text-white flex items-center justify-center text-[28px]">
          ✓
        </div>

        <h1 className="mt-5 text-[34px] md:text-[38px] leading-none font-bold text-[#333]">
          {t.thanks}
        </h1>

        <p className="mt-4 text-[15px] text-[#666]">
          {lang === "EN"
            ? "Your order has been received successfully."
            : "تم استلام طلبك بنجاح."}
        </p>

        {order.order_number && (
          <p className="mt-3 inline-flex bg-white border border-[#d8d8d8] px-4 py-2 text-[14px] text-black">
            {lang === "EN" ? "Order" : "الطلب"} #{order.order_number}
          </p>
        )}
      </div>
    </section>

    {/* CONTENT */}
    <section className="bg-[#F6EFE9] pb-14 md:pb-16">
      <div className="max-w-[1350px] mx-auto px-5 md:px-6 pt-6 md:pt-8">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_390px] gap-6 md:gap-8 items-start">
          {/* LEFT */}
          <div className="space-y-6">
            {/* LOGIN INFORMATION */}
            <div className={cardStyle}>
              <h2 className="text-[22px] md:text-[20px] font-normal text-[#333] mb-5">
                {t.loginInfo}
              </h2>

              <div className="space-y-4">
                <div className="flex items-start justify-between gap-5 text-[14px]">
                  <span className="text-[#777]">{t.name}</span>
                  <span className="font-semibold text-black text-right">
                    {order.full_name}
                  </span>
                </div>

                <div className="flex items-start justify-between gap-5 text-[14px]">
                  <span className="text-[#777]">{t.phone}</span>
                  <span className="font-semibold text-black text-right" dir="ltr">
                    {order.phone}
                  </span>
                </div>

                <div className="flex items-start justify-between gap-5 text-[14px]">
                  <span className="text-[#777]">{t.email}</span>
                  <span className="font-semibold text-black text-right break-all">
                    {order.email || t.noEmail}
                  </span>
                </div>

                {order.shipping_method !== "pickup" && (
                  <>
                    <div className="flex items-start justify-between gap-5 text-[14px]">
                      <span className="text-[#777]">{t.city}</span>
                      <span className="font-semibold text-black text-right">
                        {order.city}
                      </span>
                    </div>

                    <div className="flex items-start justify-between gap-5 text-[14px]">
                      <span className="text-[#777]">{t.address}</span>
                      <span className="font-semibold text-black text-right max-w-[60%]">
                        {order.street}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 bg-[#F6EFE9] px-4 py-3 text-[13px] leading-[1.6] text-[#555]">
                {t.credentialsNote}
              </div>
            </div>

            {/* DELIVERY MESSAGE */}
            <div className={cardStyle}>
              <h2 className="text-[22px] md:text-[20px] font-normal text-[#333] mb-4">
                {lang === "EN" ? "What happens next?" : "ماذا بعد؟"}
              </h2>

              <p className="text-[15px] leading-[1.7] text-black">
                {getDeliveryMessage()}
              </p>

              <p className="mt-4 text-[13px] leading-[1.6] text-[#666]">
                {lang === "EN"
                  ? "Our team will contact you if we need any extra information about your order."
                  : "سيتواصل معك فريقنا إذا احتجنا إلى أي معلومات إضافية حول الطلب."}
              </p>
            </div>
          </div>

          {/* RIGHT SUMMARY */}
          <aside className={`${cardStyle} md:sticky md:top-[160px]`}>
            <h2 className="text-[22px] md:text-[20px] font-normal text-[#333] mb-5">
              {t.summary}
            </h2>

            <div className="space-y-3">
              <div className="flex justify-between text-[14px] text-black">
                <span>{t.products}</span>
                <span>{order.itemCount}</span>
              </div>

              <div className="flex justify-between text-[14px] text-black">
                <span>{lang === "EN" ? "Subtotal" : "المجموع"}</span>
                <span>${Number(order.subtotal || 0).toFixed(2)}</span>
              </div>

              {order.order_discount > 0 && (
                <div className="flex justify-between text-[14px] text-green-700">
                  <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
                  <span>- ${Number(order.order_discount).toFixed(2)}</span>
                </div>
              )}

              {order.discount > 0 && (
                <div className="flex justify-between text-[14px] text-green-700">
                  <span>{lang === "AR" ? "كوبون" : "Discount"}</span>
                  <span>- ${Number(order.discount).toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-[14px] text-black">
                <span>
                  {order.shipping_method === "pickup"
                    ? lang === "EN"
                      ? "Pickup from store"
                      : "استلام من المتجر"
                    : t.delivery}
                </span>

                <span
                  className={
                    order.shipping_method === "pickup" || Number(order.delivery_fee || 0) === 0
                      ? "font-semibold text-green-700"
                      : "font-semibold text-black"
                  }
                >
                  {order.shipping_method === "pickup"
                    ? lang === "EN"
                      ? "Free"
                      : "مجاني"
                    : Number(order.delivery_fee || 0) === 0
                    ? lang === "EN"
                      ? "Free"
                      : "مجاني"
                    : `$${Number(order.delivery_fee || 0).toFixed(2)}`}
                </span>
              </div>
            </div>

            <div className="my-5 border-t border-black" />

            <div className="flex justify-between items-center text-[21px] font-bold text-black">
              <span>{t.total}</span>
              <span>${Number(order.total || 0).toFixed(2)}</span>
            </div>

            <button
              type="button"
              onClick={() => {
                clearCart();
                sessionStorage.removeItem("esill-order");
                navigate("/");
              }}
              className="
                mt-7
                w-full h-[52px]
                bg-[#e99a22]
                text-white
                text-[16px]
                font-normal
                hover:opacity-90
                active:scale-[0.99]
                transition
              "
            >
              {t.ok}
            </button>

            <button
              type="button"
              onClick={() => navigate("/all-products")}
              className="
                mt-3
                w-full h-[48px]
                bg-white
                border border-black
                text-black
                text-[15px]
                font-normal
                hover:bg-[#f7f7f7]
                transition
              "
            >
              {lang === "EN" ? "Continue shopping" : "متابعة التسوق"}
            </button>

            <div className="mt-6 bg-[#F6EFE9] px-4 py-3 text-[13px] leading-[1.6] text-[#555]">
              {lang === "EN"
                ? "Thank you for shopping with us."
                : "شكراً لتسوقك معنا."}
            </div>
          </aside>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);
}

export default ThankYouPage;